'use client';

import { useEffect } from 'react';
import { useStorageKeyPrefix } from './StorageKeyPrefixProvider';
import useStorage_InternalUseOnly, {
  UseStorageOptions,
} from './useStorage_InternalUseOnly';
import { deserialize } from '../../../../utils/serialize';

interface LocalStorageOptions extends UseStorageOptions {
  /**
   * If true, the key will not be prefixed (with what is commonly the user ID).
   * In general, local storage data should be prefixed to avoid sharing data
   * between users who share the same computer.
   */
  noPrefixedKey?: boolean;
}

export default function useLocalStorage<T>(
  key: string,
  fallbackValue: T,
  options: LocalStorageOptions = {},
) {
  const prefix = useStorageKeyPrefix();
  const shouldPrefix = options.noPrefixedKey !== true;
  const prefixedKey = shouldPrefix ? `${prefix}:${key}` : key;
  const [value, setValue] = useStorage_InternalUseOnly(
    'local',
    prefixedKey,
    fallbackValue,
    options,
  );

  // Syncs local state value across tabs
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === prefixedKey && event.newValue != null) {
        setValue(deserialize(event.newValue));
      }
    }

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }, [prefixedKey, setValue]);

  return [value, setValue] as const;
}
